/**
 * Program 12.1 UX3 — EPC Lead cockpit (pack-gated on LeadDetailPage).
 */
import React, { useMemo } from 'react';
import { Alert, Button, Card, Col, List, Progress, Row, Space, Tag, Typography } from 'antd';
import type { Lead } from '@/services/lead.service';
import { formatDate, formatMoney } from '@/utils/format';
import {
  buildLeadAttention,
} from './leadAttention';
import {
  buildLeadReadiness,
  leadConversionCarryForward,
  leadConversionRequires,
  resolveLeadPrimaryCta,
} from './leadReadiness';

const { Text, Title, Paragraph } = Typography;

export type EpcLeadCockpitProps = {
  lead: Lead;
  ownerLabel?: string;
  onPrimaryAction?: (key: ReturnType<typeof resolveLeadPrimaryCta>['key']) => void;
  onOpenConversionPreview?: () => void;
};

function readinessIcon(state: string): string {
  if (state === 'established') return '✓';
  if (state === 'partial') return '△';
  return '✕';
}

export const EpcLeadCockpit: React.FC<EpcLeadCockpitProps> = ({
  lead,
  ownerLabel = '—',
  onPrimaryAction,
  onOpenConversionPreview,
}) => {
  const row = lead as unknown as Record<string, unknown>;
  const readiness = useMemo(() => buildLeadReadiness(row), [lead]);
  const attention = useMemo(() => buildLeadAttention(row), [lead]);
  const cta = useMemo(() => resolveLeadPrimaryCta(row), [lead]);

  const projectTitle =
    lead.description?.trim() ||
    lead.business_challenge?.trim() ||
    lead.customer_name;

  const valueDisplay =
    lead.estimated_project_value != null && Number(lead.estimated_project_value) > 0
      ? formatMoney(Number(lead.estimated_project_value), { currency: 'AED' })
      : lead.annual_revenue != null && Number(lead.annual_revenue) > 0
        ? formatMoney(Number(lead.annual_revenue), { currency: 'AED' })
        : 'Value not established';

  const timingLabel = lead.next_follow_up_date
    ? formatDate(lead.next_follow_up_date)
    : lead.expected_timeline_weeks
      ? `${lead.expected_timeline_weeks} weeks (indicative)`
      : '—';

  const handlePrimary = () => {
    if (cta.key === 'convert' && onOpenConversionPreview) {
      onOpenConversionPreview();
      return;
    }
    onPrimaryAction?.(cta.key);
  };

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Card>
        <Title level={4} style={{ marginTop: 0, marginBottom: 4 }}>
          {projectTitle}
        </Title>
        <Text type="secondary">
          {lead.customer_name}
          {lead.industry ? ` · ${lead.industry}` : ''}
          {lead.country ? ` · ${lead.country}` : ''}
        </Text>
        <Row gutter={[24, 12]} style={{ marginTop: 16 }}>
          <Col xs={12} sm={6}>
            <Text type="secondary">Signal Quality</Text>
            <div>
              <Tag color={readiness.signalQuality === 'HIGH' ? 'green' : readiness.signalQuality === 'MEDIUM' ? 'blue' : 'default'}>
                {readiness.signalQuality}
              </Tag>
            </div>
          </Col>
          <Col xs={12} sm={6}>
            <Text type="secondary">Potential Value</Text>
            <div>{valueDisplay}</div>
          </Col>
          <Col xs={12} sm={6}>
            <Text type="secondary">Next Action</Text>
            <div>{timingLabel}</div>
          </Col>
          <Col xs={12} sm={6}>
            <Text type="secondary">Owner</Text>
            <div>{ownerLabel}</div>
          </Col>
        </Row>
        <div style={{ marginTop: 16 }}>
          <Button type="primary" onClick={handlePrimary} disabled={cta.disabled}>
            {cta.label}
          </Button>
        </div>
      </Card>

      {attention.length > 0 && (
        <Card title="Attention" size="small">
          <List
            size="small"
            dataSource={attention}
            renderItem={(a) => (
              <List.Item>
                <Space>
                  <Tag color={a.severity === 'critical' ? 'red' : a.severity === 'warning' ? 'orange' : 'blue'}>
                    {a.severity.toUpperCase()}
                  </Tag>
                  <Text>{a.title}</Text>
                </Space>
                {a.detail && (
                  <Paragraph type="secondary" style={{ margin: 0, fontSize: 12 }}>
                    {a.detail}
                  </Paragraph>
                )}
              </List.Item>
            )}
          />
        </Card>
      )}

      <Card title="Lead Readiness" size="small">
        <Progress
          percent={Math.round((readiness.establishedCount / readiness.total) * 100)}
          format={() => `${readiness.establishedCount} / ${readiness.total} established`}
          style={{ marginBottom: 12 }}
        />
        <List
          size="small"
          dataSource={readiness.items}
          renderItem={(item) => (
            <List.Item>
              <Space>
                <Text>{readinessIcon(item.state)}</Text>
                <Text>{item.label}</Text>
                {item.detail && <Text type="secondary">({item.detail})</Text>}
              </Space>
            </List.Item>
          )}
        />
      </Card>

      {cta.key === 'convert' && (
        <Alert
          type="info"
          showIcon
          message="Conversion preview"
          description={
            <>
              <Text>Will carry forward: {leadConversionCarryForward(row).join(', ') || '—'}</Text>
              <br />
              <Text type="secondary">Requires: {leadConversionRequires().join(' · ')}</Text>
            </>
          }
        />
      )}
    </Space>
  );
};

export default EpcLeadCockpit;
